import React, { createContext, useContext, useState, useCallback } from "react";
import { CheckCircleIcon, XCircleIcon, XIcon } from "lucide-react";
import { cn } from "@util/tailwind";

interface SnackbarMessage {
    id: string;
    type: "success" | "error";
    title: string;
    description?: string;
    duration?: number;
    actionLabel?: string;
    onAction?: () => void;
}

interface SnackbarContextType {
    showSuccess: (title: string, description?: string, duration?: number, options?: { actionLabel?: string; onAction?: () => void }) => void;
    showError: (title: string, description?: string, duration?: number) => void;
    removeSnackbar: (id: string) => void;
}

const SnackbarContext = createContext<SnackbarContextType | undefined>(
    undefined
);

export function useSnackbar() {
    const context = useContext(SnackbarContext);
    if (!context) {
        throw new Error("useSnackbar must be used within a SnackbarProvider");
    }
    return context;
}

interface SnackbarProviderProps {
    children: React.ReactNode;
}

export function SnackbarProvider({ children }: SnackbarProviderProps) {
    const [messages, setMessages] = useState<SnackbarMessage[]>([]);

    const removeSnackbar = useCallback((id: string) => {
        setMessages((prev) => prev.filter((message) => message.id !== id));
    }, []);

    const addSnackbar = useCallback(
        (message: Omit<SnackbarMessage, "id">) => {
            const id = Math.random().toString(36).substr(2, 9);
            const newMessage = { ...message, id };

            setMessages((prev) => [...prev, newMessage]);

            // Auto-remove after duration
            setTimeout(() => {
                removeSnackbar(id);
            }, message.duration || 5000);

            return id;
        },
        [removeSnackbar]
    );

    const showSuccess = useCallback(
        (title: string, description?: string, duration?: number, options?: { actionLabel?: string; onAction?: () => void }) => {
            addSnackbar({ type: "success", title, description, duration, actionLabel: options?.actionLabel, onAction: options?.onAction });
        },
        [addSnackbar]
    );

    const showError = useCallback(
        (title: string, description?: string, duration?: number) => {
            addSnackbar({ type: "error", title, description, duration });
        },
        [addSnackbar]
    );

    return (
        <SnackbarContext.Provider
            value={{ showSuccess, showError, removeSnackbar }}
        >
            {children}
            <SnackbarContainer messages={messages} onRemove={removeSnackbar} />
        </SnackbarContext.Provider>
    );
}

interface SnackbarContainerProps {
    messages: SnackbarMessage[];
    onRemove: (id: string) => void;
}

function SnackbarContainer({ messages, onRemove }: SnackbarContainerProps) {
    return (
        <div className="fixed top-4 right-4 z-50 space-y-2">
            {messages.map((message) => (
                <SnackbarItem key={message.id} message={message} onRemove={onRemove} />
            ))}
        </div>
    );
}

interface SnackbarItemProps {
    message: SnackbarMessage;
    onRemove: (id: string) => void;
}

function SnackbarItem({ message, onRemove }: SnackbarItemProps) {
    const [isVisible, setIsVisible] = useState(false);

    React.useEffect(() => {
        // Trigger animation
        const timer = setTimeout(() => setIsVisible(true), 10);
        return () => clearTimeout(timer);
    }, []);

    const handleRemove = () => {
        setIsVisible(false);
        setTimeout(() => onRemove(message.id), 300);
    };

    return (
        <div
            className={cn(
                "transform transition-all duration-300 ease-in-out",
                isVisible ? "translate-x-0 opacity-100" : "translate-x-full opacity-0"
            )}
        >
            <div
                className={cn(
                    "min-w-80 max-w-md rounded-lg shadow-lg border p-4 bg-white",
                    message.type === "success" && "border-green-200",
                    message.type === "error" && "border-red-200"
                )}
            >
                <div className="flex items-center gap-3">
                    <div className="flex-shrink-0">
                        {message.type === "success" ? (
                            <CheckCircleIcon className="h-5 w-5 text-green-500" />
                        ) : (
                            <XCircleIcon className="h-5 w-5 text-red-500" />
                        )}
                    </div>

                    <div className="flex-1 min-w-0">
                        <h4
                            className={cn(
                                "text-sm font-medium",
                                message.type === "success" && "text-green-700",
                                message.type === "error" && "text-red-700"
                            )}
                        >
                            {message.title}
                            {message.actionLabel && message.onAction && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        message.onAction?.();
                                        handleRemove();
                                    }}
                                    className="ml-2 text-sm font-semibold text-blue-600 hover:text-blue-800 underline cursor-pointer"
                                >
                                    {message.actionLabel}
                                </button>
                            )}
                        </h4>
                        {message.description && (
                            <p
                                className={cn(
                                    "mt-1 text-sm",
                                    message.type === "success" && "text-green-700",
                                    message.type === "error" && "text-red-700"
                                )}
                            >
                                {message.description}
                            </p>
                        )}
                    </div>

                    <button
                        onClick={handleRemove}
                        className="flex-shrink-0 rounded-md p-1.5 hover:bg-gray-100 transition-colors"
                    >
                        <XIcon className="h-4 w-4 text-gray-400" />
                    </button>
                </div>
            </div>
        </div>
    );
}
